/** Field-level context help for the notification suite's complex forms.
 *  Rendered by <FieldHelp> (popover + "Learn more" into the help articles).
 *  Keep bodies to 1–3 sentences — the linked story carries the depth. */
import type { FieldHelpEntry } from '@/components/FieldHelp';

// ---- Block form dialog ------------------------------------------------------

export const BLOCK_HELP = {
  codename: {
    title: 'Codename',
    body: 'The identifier templates use to reference this block as block.<codename>. Letters, digits and underscores; stable once referenced — renaming breaks templates that use it.',
    story: 'notification-blocks',
  },
  name: {
    title: 'Name',
    body: 'Optional human-readable label shown in lists and pickers. Has no effect on rendering.',
    story: 'notification-blocks',
  },
  description: {
    title: 'Description',
    body: 'Notes for other ops people — what the block contains and when to use it. Never rendered to guests.',
    story: 'notification-blocks',
  },
  operator: {
    title: 'Operator',
    body: 'Which tour operator owns this block. Leave empty for a system-wide block available to every operator.',
    story: 'notification-blocks',
  },
  type: {
    title: 'Type',
    body: 'How the value is produced: a rendered template, a ruleset decision, a dotty/JSONPath lookup into the delivery context, a registered function, or a constant.',
    story: 'notification-blocks',
  },
  errorMode: {
    title: 'Error mode',
    body: 'Soft: an evaluation failure is logged and the default value is used. Hard: a failure aborts the whole delivery — no message rather than a wrong one. Use hard sparingly.',
    story: 'notification-blocks',
  },
  feature: {
    title: 'Feature',
    body: 'Ties the block to a named content feature. The block renders only where that feature is enabled at every applicable level (system, OTA, tenant, tour, notification); otherwise it renders empty.',
    story: 'notification-feature-flags',
  },
  definition: {
    title: 'Definition',
    body: 'The type-specific source of the value — a ruleset or function reference, or the constant itself.',
    story: 'notification-blocks',
  },
  definitionTemplate: {
    title: 'Template source',
    body: 'A Django template rendered against the delivery context. The HTML (default) tab is the base body; Markdown/Plaintext tabs override it for those target formats and fall back to the base when empty.',
    story: 'django-templates',
  },
  definitionJsonpath: {
    title: 'JSONPath expression',
    body: 'A JSONPath query into the delivery context, e.g. $.booking.guest.first_name — with [*] wildcards and ?(@…) filters when a plain path is not enough.',
    story: 'jsonpath-syntax',
  },
  definitionDotty: {
    title: 'Dotty path',
    body: 'A dot-separated walk into the delivery context, e.g. booking.guest.first_name. List items are indexed with a number segment (tags.0.value).',
    story: 'dotty-path-syntax',
  },
  definitionRule: {
    title: 'Rules',
    body: 'The same rules the ruleset editor offers, stored inside this block — use it for a one-off condition (a seasonal date window, say) instead of creating a standalone ruleset. The block evaluates to true or false, read in templates as {% if block.<codename> %}.',
    story: 'notification-blocks',
  },
  definitionTag: {
    title: 'Ephemeral tag',
    body: 'Injects the referenced tags into the rendering context under this block, optionally only when a condition matches. Wording stays in the tag itself (translations and schema included); the block only decides when it appears. Read it with {% pick block.<codename> "<category>" as v %} so templates fall through to their normal source when the condition fails.',
    story: 'notification-blocks',
  },
  channelOverrides: {
    title: 'Channel overrides',
    body: 'Replace the rendered value for one delivery channel. Delivery picks a channel override first, then the target format definition, then the HTML (default) body.',
    story: 'notification-blocks',
  },
  templateLanguage: {
    title: 'Template language',
    body: 'The template engine the definition is written in — Django Template Language by default.',
    story: 'django-templates',
  },
  isJson: {
    title: 'Parse the value as JSON',
    body: 'Decode the constant as JSON so templates receive a structured value (list or object) instead of a plain string.',
    story: 'notification-blocks',
  },
  defaultValue: {
    title: 'Default value',
    body: 'Fallback used when evaluation fails in soft mode or produces nothing.',
    story: 'notification-blocks',
  },
  enabled: {
    title: 'Enabled',
    body: 'A disabled block evaluates to nothing everywhere it is referenced — templates keep working, the content just disappears.',
    story: 'notification-blocks',
  },
} as const satisfies Record<string, FieldHelpEntry>;

// ---- Language policy dialog -------------------------------------------------

export const POLICY_HELP = {
  codename: {
    title: 'Codename',
    body: 'The identifier notifications and nested rules use to reference this policy.',
    story: 'language-policies',
  },
  operator: {
    title: 'Operator',
    body: 'Which tour operator owns this policy. Leave empty for a system-wide policy. The scope also drives what the rule pickers (blocks, nested policies) search.',
    story: 'language-policies',
  },
  enabled: {
    title: 'Enabled',
    body: 'A disabled policy is skipped — notifications pointing at it fall back to the standard language resolution chain.',
    story: 'language-policies',
  },
  rules: {
    title: 'Rules',
    body: 'Evaluated top to bottom; the first rule returning a usable language wins. Raw values normalize through Language Aliases; unrecognized results fall through to the next rule, ultimately to the standard fallback chain. End with a constant rule as the catch-all.',
    story: 'language-policies',
  },
  ruleType: {
    title: 'Rule type',
    body: 'Where this rule reads the language from: a JSONPath/dotty lookup into the delivery context, a rendered template, a registered function, a fixed language, a notification block, or another policy nested inside this one.',
    story: 'language-policies',
  },
  ruleJsonpath: {
    title: 'JSONPath expression',
    body: 'A JSONPath query into the delivery context; the matched value is taken as the language and normalized through aliases — e.g. $.booking.guest.language.',
    story: 'jsonpath-syntax',
  },
  ruleDotty: {
    title: 'Dotty path',
    body: 'A dot-separated lookup into the delivery context; the value is taken as the language and normalized through aliases — e.g. booking.guest.language.',
    story: 'dotty-path-syntax',
  },
  ruleTemplate: {
    title: 'Template source',
    body: 'A Django template rendered against the delivery context; its whole output is taken as the language value, then normalized through aliases.',
    story: 'django-templates',
  },
} as const satisfies Record<string, FieldHelpEntry>;

// ---- Language alias dialog --------------------------------------------------

export const ALIAS_HELP = {
  alias: {
    title: 'Alias',
    body: 'The raw string as it appears in the data — a tag value like "Português" or "Anglais". Matching is what turns free-form values into a usable language.',
    story: 'language-policies',
  },
  language: {
    title: 'Language',
    body: 'The ISO language code the alias resolves to. Policies and standard resolution both consult aliases when normalizing values.',
    story: 'language-policies',
  },
  operator: {
    title: 'Operator',
    body: 'Which tour operator the alias applies to. Leave empty for a system-wide alias.',
    story: 'language-policies',
  },
  enabled: {
    title: 'Enabled',
    body: 'Disabled aliases are ignored during normalization.',
    story: 'language-policies',
  },
} as const satisfies Record<string, FieldHelpEntry>;

// ---- Theme dialog -----------------------------------------------------------

export const THEME_HELP = {
  codename: {
    title: 'Codename',
    body: 'Referenced by notifications and by the operator config default-theme setting.',
    story: 'notification-themes',
  },
  operator: {
    title: 'Operator',
    body: 'Which tour operator owns the theme. Leave empty for a system-wide theme any operator can use.',
    story: 'notification-themes',
  },
  emailShell: {
    title: 'Email shell',
    body: 'A Django template document wrapped around every rendered email body — the branded frame. It must contain the {{ body }} insertion point; empty means no wrapping.',
    story: 'django-templates',
  },
  tokens: {
    title: 'Design tokens (JSON)',
    body: 'A JSON object of design values (colors, spacing) exposed to templates as theme.* and deep-merged over the operator color theme.',
    story: 'notification-themes',
  },
  inlineCss: {
    title: 'Inline CSS',
    body: 'Inline the stylesheet into element style attributes at send time — required by many email clients that strip <style> blocks.',
    story: 'notification-themes',
  },
  signatures: {
    title: 'Text-channel signatures',
    body: 'A Django template snippet appended to SMS/chat/WhatsApp/Airbnb message bodies at delivery — the text-channel equivalent of the email shell.',
    story: 'django-templates',
  },
  enabled: {
    title: 'Enabled',
    body: 'A disabled theme stops wrapping deliveries; notifications pointing at it render unthemed.',
    story: 'notification-themes',
  },
} as const satisfies Record<string, FieldHelpEntry>;

// ---- Notification editor ----------------------------------------------------

export const NOTIFICATION_HELP = {
  enabled: {
    title: 'Enabled',
    body: 'Master switch — a disabled notification never fires, whatever its targeting says.',
    story: 'notification-grid-editor',
  },
  composition: {
    title: 'Composition',
    body: 'The ordered block references that feed the block.* namespace when this notification renders through the standard layout path.',
    story: 'notification-blocks',
  },
  disableDefaultBlocks: {
    title: 'Disable default blocks',
    body: 'When on, only the references listed here feed block.* — the tenant/system default block lists are skipped entirely.',
    story: 'notification-blocks',
  },
  standardTemplate: {
    title: 'Standard template (layout)',
    body: 'The layout the referenced blocks compose into on this channel. Empty uses the channel default. While the standard path is active, the custom per-channel sources are ignored.',
    story: 'notification-grid-editor',
  },
  renderingType: {
    title: 'Rendering path',
    body: 'Standard: blocks compose into the selected layout. Custom: this channel renders the hand-written template sources below instead.',
    story: 'notification-grid-editor',
  },
} as const satisfies Record<string, FieldHelpEntry>;

/** Delivery-method field help, keyed by the NDM model field name. The shared
 *  ChannelSettingsPanel widgets look their `field` up here — adding an entry
 *  is all it takes to give a delivery field a popover. */
export const DM_FIELD_HELP: Record<string, FieldHelpEntry> = {
  email_destination: {
    title: 'Destination',
    body: 'Who receives the email: the triggering user, a support contact, a fixed address, an OTA contact, or a conversation audience.',
    story: 'notification-grid-editor',
  },
  sms_destination: {
    title: 'Destination',
    body: 'Who receives the SMS: the triggering user, a support contact, a fixed number, an OTA contact, or a conversation audience.',
    story: 'notification-grid-editor',
  },
  whatsapp_destination: {
    title: 'Destination',
    body: 'Who receives the WhatsApp message: the triggering user, a support contact, a fixed number, an OTA contact, or a conversation audience.',
    story: 'notification-grid-editor',
  },
  email_address: {
    title: 'Arbitrary email address',
    body: 'Used only while Destination is "Arbitrary address" — every delivery goes to this fixed address instead of a per-user one.',
    story: 'notification-grid-editor',
  },
  sms_number: {
    title: 'Arbitrary phone number',
    body: 'Used only while Destination is "Arbitrary number" — every delivery goes to this fixed number.',
    story: 'notification-grid-editor',
  },
  whatsapp_number: {
    title: 'Arbitrary phone number',
    body: 'Used only while Destination is "Arbitrary number" — every delivery goes to this fixed number.',
    story: 'notification-grid-editor',
  },
  email_allow_override: {
    title: 'Allow event override',
    body: 'Lets the triggering event supply a different recipient than the user record — e.g. a per-booking contact provided by the integration.',
    story: 'notification-grid-editor',
  },
  sms_allow_override: {
    title: 'Allow event override',
    body: 'Lets the triggering event supply a different recipient number than the user record.',
    story: 'notification-grid-editor',
  },
  whatsapp_allow_override: {
    title: 'Allow event override',
    body: 'Lets the triggering event supply a different recipient number than the user record.',
    story: 'notification-grid-editor',
  },
  email_replyto: {
    title: 'Reply-to',
    body: 'The Reply-To address stamped on the sent email. Empty uses the operator default.',
    story: 'notification-grid-editor',
  },
  email_append_conversation_sid: {
    title: 'Append conversation SID',
    body: 'Tags the outgoing email with the guest conversation reference so an inbox reply threads back into the in-app chat.',
    story: 'blacklist-redirects',
  },
  prevent_replies: {
    title: 'Prevent replies',
    body: 'Incoming replies to this message are not accepted into the conversation; the auto-response below is sent instead.',
    story: 'notification-grid-editor',
  },
  prevent_replies_message: {
    title: 'Auto-response',
    body: 'Sent back to anyone who replies while replies are prevented.',
    story: 'notification-grid-editor',
  },
  chat_destination: {
    title: 'Destination',
    body: 'Which conversation receives the chat message — the main office thread, the active tour, the event conversation, a fixed SID, or a group/internal/announcements channel.',
    story: 'notification-grid-editor',
  },
  chat_conversation_sid: {
    title: 'Arbitrary conversation SID',
    body: 'Used only while Destination is "Arbitrary conversation" — the exact conversation to post into.',
    story: 'notification-grid-editor',
  },
  chat_origin: {
    title: 'Message sender',
    body: 'Whether the message appears as an automated system message or as coming from a specific user (picked below).',
    story: 'notification-grid-editor',
  },
  airbnb_destination: {
    title: 'Destination',
    body: 'Which Airbnb thread receives the message — the one associated with the event conversation, or a fixed thread ID.',
    story: 'notification-grid-editor',
  },
  airbnb_thread_id: {
    title: 'Airbnb thread ID',
    body: 'Used only while Destination is "Arbitrary Airbnb thread".',
    story: 'notification-grid-editor',
  },
  email_attach_event_files: {
    title: 'Event/calendar invite',
    body: 'Embeds an event/calendar invite into the email.',
    story: 'notification-grid-editor',
  },
  email_attach_booking_voucher: {
    title: 'Booking voucher',
    body: 'Attaches the voucher provided by the booking system. Attachments survive format clamps (e.g. an OTA forcing plain text).',
    story: 'ota-format-overrides',
  },
  email_attach_booking_invoice: {
    title: 'Booking invoice',
    body: 'Attaches the invoice provided by the booking system.',
    story: 'notification-grid-editor',
  },
  email_attach_participant_files: {
    title: 'Participant files',
    body: "Sends files attached to the specific participant's booking.",
    story: 'notification-grid-editor',
  },
  email_attach_notification_files: {
    title: 'Notification files',
    body: 'Generic attachments sent to every participant this notification triggers for.',
    story: 'notification-grid-editor',
  },
  http_url: {
    title: 'Target URL',
    body: 'The webhook endpoint this delivery calls. Template variables are allowed in the request data, not in the URL.',
    story: 'notification-grid-editor',
  },
  http_response_status: {
    title: 'Expected status',
    body: 'The HTTP status treated as success; anything else counts as a failed delivery and retries per the retry policy.',
    story: 'notification-grid-editor',
  },
  sns_topic: {
    title: 'SNS topic ARN',
    body: 'The AWS SNS topic this delivery publishes to.',
    story: 'notification-grid-editor',
  },
  sentry_tag_key: {
    title: 'Sentry tag key',
    body: 'Tag stamped on the Sentry event for filtering.',
    story: 'notification-grid-editor',
  },
  sentry_tag_value: {
    title: 'Sentry tag value',
    body: 'Value for the Sentry tag key.',
    story: 'notification-grid-editor',
  },
};

// ---- Rules editor (notification + ruleset) ---------------------------------

export const RULES_HELP = {
  matchType: {
    title: 'Match mode',
    body: 'ALL: every rule must pass for the notification/ruleset to match. ANY: one passing rule is enough. With no rules, everything matches.',
    story: 'notification-rulesets',
  },
  negate: {
    title: 'NOT',
    body: 'Inverts the rule: it passes exactly when the condition does not hold. Applied after the rule evaluates.',
    story: 'notification-rulesets',
  },
  includeRuleset: {
    title: 'Include ruleset',
    body: 'Evaluates a reusable ruleset in place of an inline condition. The ruleset must be a system ruleset or belong to the same operator; include-cycles are rejected on save.',
    story: 'notification-rulesets',
  },
  exprField: {
    title: 'Expression: field',
    body: 'A Django template rendered against the notification context — e.g. {{ participant.pax_count }} or {% if order.bookings %}x{% endif %}. The rendered STRING is what gets compared.',
    story: 'django-templates',
  },
  exprOp: {
    title: 'Expression: operator',
    body: 'equals compares lowercased strings. The numeric operators (>, ≥, <, ≤) float-coerce both sides and fail the rule when either does not parse. contains is a case-sensitive substring check. "is true" means the field renders to a NON-EMPTY string (beware: "False" is non-empty — normalize with {% if %} or |yesno:"x," first); "is false" means it renders empty.',
    story: 'notification-rulesets',
  },
  exprValue: {
    title: 'Expression: value',
    body: 'The right-hand side — also rendered as a Django template, so it can be a constant ("10") or reference context ({{ tour.name }}). Unused for "is true" / "is false".',
    story: 'django-templates',
  },
  deliveryMethodAllowUndefined: {
    title: 'Ignore when method unknown',
    body: 'Some evaluation points have no delivery method in scope yet. On: the rule passes there. Off: the rule fails there.',
    story: 'notification-rulesets',
  },
  fieldExistsModes: {
    title: 'Field & value modes',
    body: 'Path resolves a dotty context path ("order.bookings.0.pickup"). Template renders DTL. Constant uses the text as-is.',
    story: 'dotty-path-syntax',
  },
} satisfies Record<string, FieldHelpEntry>;

export const RULESET_HELP = {
  codename: {
    title: 'Codename',
    body: 'Optional stable alias, unique per operator (case-insensitive). Usable in {% if-ruleset %} / {% ruleset %} template tags and in boolean expressions ("vip and not blocked"). "and", "or", "not" are reserved.',
    story: 'notification-rulesets',
  },
  tenant: {
    title: 'Operator',
    body: 'Which tour operator owns the ruleset. System rulesets (no operator) are usable by every operator; tenant rulesets only by their own.',
    story: 'notification-rulesets',
  },
} satisfies Record<string, FieldHelpEntry>;

// ---- Targeting editor -------------------------------------------------------

export const TARGETING_HELP = {
  event: {
    title: 'Event',
    body: 'The platform event this notification fires on. Changing it changes when the notification is sent AND which context variables the templates see. Custom codes are allowed but only fire if something emits them.',
    story: 'notification-grid-editor',
  },
  triggeredBy: {
    title: 'Triggered by',
    body: 'Staff roles whose participants/users this notification applies to. "Guests" covers participants without a staff role; "Observers" covers observer bookings. With nothing selected the notification is not role-restricted.',
    story: 'notification-grid-editor',
  },
  bookingState: {
    title: 'Booking state',
    body: 'Only participants whose booking is exactly in this state match. Checked only for tour_participant.* and order.* events.',
    story: 'notification-grid-editor',
  },
  tourOverrideMode: {
    title: 'Tour override mode',
    body: '"Send always" ignores tour-specific overrides; "Send if no tour-specific override" (default) yields to a tour-specific variant; "Tour-specific override" fires only for the tours listed and replaces the default.',
    story: 'notification-grid-editor',
  },
  deliveryLock: {
    title: 'Delivery lock',
    body: 'When on, the same user receives this notification at most once per window; further deliveries inside the window are logged as rejected.',
    story: 'notification-grid-editor',
  },
  ignoreUserPrefs: {
    title: 'Ignore user notification preferences',
    body: 'Delivers even to users who opted out of this kind of notification. Use sparingly — this overrides an explicit user choice.',
    story: 'notification-grid-editor',
  },
  tags: {
    title: 'Tags',
    body: 'Participant tags this notification is restricted to. Distinct from rule-based tag conditions — this is the coarse M2M filter the dashboard calls "System tags".',
    story: 'notification-grid-editor',
  },
  accessTags: {
    title: 'Access tags',
    body: 'Restricts which staff can see/edit this notification via access-group scoping. Does not affect guest targeting.',
    story: 'notification-grid-editor',
  },
} as const satisfies Record<string, FieldHelpEntry>;

export const ADVANCED_RULES_HELP = {
  ordering: {
    title: 'Delivery method ordering',
    body: '"Use all" sends every enabled method; "Stop at first successful" walks them in order until one succeeds; "Advanced" hands control to the advanced rules below.',
    story: 'notification-grid-editor',
  },
  rule: {
    title: 'Advanced delivery rule',
    body: 'If [any/all] of the trigger methods report the condition, the dependent methods are sent after the delay. Read/Unread support depends on the channel (SMS cannot report reads; Email uses open tracking).',
    story: 'notification-grid-editor',
  },
} as const satisfies Record<string, FieldHelpEntry>;

export const AUTO_REPLY_HELP = {
  enabled: {
    title: 'Send another message',
    body: "When a guest replies to a message this notification produced, the first option whose trigger matches sends its reply back on the same channel and optionally applies a tag.",
    story: 'notification-grid-editor',
  },
  aiTrigger: {
    title: 'Use AI',
    body: 'Instead of an exact (case-insensitive) answer match, the trigger becomes an AI prompt — the option matches when the model judges the guest reply satisfies it.',
    story: 'notification-grid-editor',
  },
  applyTag: {
    title: 'Apply tag',
    body: 'Tag added to the participant when this option matches (chat_internal-category tags go to the conversation instead).',
    story: 'notification-grid-editor',
  },
  expiry: {
    title: 'Reply expires',
    body: 'Stops matching guest replies this long before (or after) the tour starts. Unchecked = replies match forever.',
    story: 'notification-grid-editor',
  },
} as const satisfies Record<string, FieldHelpEntry>;
